import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../../services/api';

const emptyEmployee = {
  first_name: '', last_name: '', employee_id: '', ssn_last_four: '', ssn_full: '',
  date_of_birth: '', phone: '', email: '',
  address: '', city: '', state: 'OH', zip_code: '',
  hire_date: new Date().toISOString().split('T')[0],
  job_title: '', certification_type: '', certification_expiry: '',
  npi: '', employee_medicaid_id: '', hourly_rate: '',
  sandata_employee_id: '', notes: '',
};

/**
 * State + handlers for the Employees page. Extracted from the formerly-521-line
 * EmployeesPage.js so the page itself becomes a thin orchestrator.
 */
export function useEmployees() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterJobTitle, setFilterJobTitle] = useState('');

  // Modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [formData, setFormData] = useState(emptyEmployee);
  const [saving, setSaving] = useState(false);
  const [focusField, setFocusField] = useState(null);

  // Import / bulk / dup
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [duplicates, setDuplicates] = useState(null);
  const [showDuplicates, setShowDuplicates] = useState(false);
  const [scanningDuplicates, setScanningDuplicates] = useState(false);
  const [mergeGroup, setMergeGroup] = useState(null);

  const fetchEmployees = useCallback(async () => {
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (filterJobTitle) params.job_title = filterJobTitle;
      const response = await api.get('/employees', { params });
      setEmployees(response.data);
    } catch {
      toast.error('Failed to load employees');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterJobTitle]);

  useEffect(() => { fetchEmployees(); }, [fetchEmployees]);

  // Deep-link: ?edit=<id>&focus=<field>
  useEffect(() => {
    const editId = searchParams.get('edit');
    const focus = searchParams.get('focus');
    if (!editId || employees.length === 0) return;
    const target = employees.find((e) => e.id === editId);
    if (!target) return;
    setEditingEmployee(target);
    setFormData({ ...target, hourly_rate: target.hourly_rate || '' });
    setFocusField(focus || null);
    setIsModalOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete('edit');
    next.delete('focus');
    setSearchParams(next, { replace: true });
  }, [employees, searchParams, setSearchParams]);

  // Once modal is open, scroll/focus the requested field
  useEffect(() => {
    if (!isModalOpen || !focusField) return;
    const id = setTimeout(() => {
      const el = document.querySelector(
        `[data-testid="employee-${focusField.replace(/_/g, '-')}"]`,
      );
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.focus();
        el.classList.add('ring-2', 'ring-amber-400', 'ring-offset-2');
        setTimeout(
          () => el.classList.remove('ring-2', 'ring-amber-400', 'ring-offset-2'),
          2400,
        );
      }
    }, 250);
    return () => clearTimeout(id);
  }, [isModalOpen, focusField]);

  const filteredEmployees = employees.filter((emp) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      emp.first_name.toLowerCase().includes(s)
      || emp.last_name.toLowerCase().includes(s)
      || emp.employee_id.toLowerCase().includes(s)
    );
  });

  const handleOpenModal = (employee = null) => {
    if (employee) {
      setEditingEmployee(employee);
      setFormData({ ...employee, hourly_rate: employee.hourly_rate || '' });
    } else {
      setEditingEmployee(null);
      setFormData(emptyEmployee);
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingEmployee(null);
    setFormData(emptyEmployee);
  };

  const handleChange = (name, value) => setFormData((prev) => ({ ...prev, [name]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...formData,
      hourly_rate: formData.hourly_rate ? parseFloat(formData.hourly_rate) : null,
    };
    try {
      if (editingEmployee) {
        await api.put(`/employees/${editingEmployee.id}`, payload);
        toast.success('Employee updated successfully');
      } else {
        await api.post('/employees', payload);
        toast.success('Employee added successfully');
      }
      handleCloseModal();
      fetchEmployees();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save employee');
    } finally {
      setSaving(false);
    }
  };

  // Lifecycle actions
  const handleActivate = async (id) => {
    try {
      await api.post(`/employees/${id}/activate`);
      toast.success('Employee activated');
      fetchEmployees();
    } catch { toast.error('Failed to activate employee'); }
  };

  const handleDeactivate = async (id) => {
    try {
      await api.post(`/employees/${id}/deactivate`);
      toast.success('Employee deactivated');
      fetchEmployees();
    } catch { toast.error('Failed to deactivate employee'); }
  };

  const handleTerminate = async (id) => {
     
    const terminationDate = window.prompt('Enter termination date (YYYY-MM-DD):');
    if (!terminationDate) return;
    try {
      await api.post(`/employees/${id}/terminate?termination_date=${terminationDate}`);
      toast.success('Employee terminated');
      fetchEmployees();
    } catch { toast.error('Failed to terminate employee'); }
  };

  const handleDelete = async (id) => {
     
    if (!window.confirm('Are you sure you want to delete this employee?')) return;
    try {
      await api.delete(`/employees/${id}`);
      toast.success('Employee deleted');
      fetchEmployees();
    } catch { toast.error('Failed to delete employee'); }
  };

  // Bulk
  const isAllSelected = filteredEmployees.length > 0 && selectedIds.length === filteredEmployees.length;
  const isSomeSelected = selectedIds.length > 0 && selectedIds.length < filteredEmployees.length;
  const toggleSelectAll = () => setSelectedIds(isAllSelected ? [] : filteredEmployees.map((e) => e.id));
  const toggleSelect = (id) => setSelectedIds(
    (prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]),
  );

  const handleBulkDelete = async () => {
    if (!selectedIds.length) return;
     
    if (!window.confirm(`Delete ${selectedIds.length} employee(s)? This cannot be undone.`)) return;
    setBulkDeleting(true);
    try {
      const res = await api.post('/employees/bulk-delete', { ids: selectedIds });
      toast.success(`Deleted ${res.data.deleted_count} employees`);
      setSelectedIds([]);
      fetchEmployees();
    } catch { toast.error('Bulk delete failed'); }
    finally { setBulkDeleting(false); }
  };

  // Duplicates
  const handleScanDuplicates = async () => {
    setScanningDuplicates(true);
    try {
      const res = await api.get('/employees/duplicates');
      setDuplicates(res.data);
      setShowDuplicates(true);
      if (res.data.total_duplicate_groups === 0) {
        toast.success('No duplicates found');
      } else {
        toast.warning(`Found ${res.data.total_duplicate_groups} duplicate group(s)`);
      }
    } catch { toast.error('Failed to scan for duplicates'); }
    finally { setScanningDuplicates(false); }
  };

  const handleSuggestSelect = (employee) => {
    // Clicking a suggestion opens the employee's record — same UX as clicking
    // "Edit" in the row actions menu, and mirrors the patient search behavior.
    // The suggest endpoint returns a trimmed projection, so look up the full
    // record from the already-fetched list before opening the modal so every
    // tab renders with the right values.
    const full = employees.find((e) => e.id === employee.id) || employee;
    handleOpenModal(full);
  };

  const getStatusBadge = (status) => ({
    Active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    'Needs completion': 'bg-amber-100 text-amber-800 border-amber-300 font-semibold',
    Inactive: 'bg-amber-50 text-amber-700 border-amber-200',
    Terminated: 'bg-red-50 text-red-700 border-red-200',
  })[status] || 'bg-slate-100 text-slate-700';

  return {
    // data
    employees, filteredEmployees, loading, fetchEmployees,
    // filters
    search, setSearch, filterStatus, setFilterStatus, filterJobTitle, setFilterJobTitle,
    handleSuggestSelect,
    // modal
    isModalOpen, editingEmployee, formData, saving,
    handleOpenModal, handleCloseModal, handleChange, handleSubmit,
    // import
    importModalOpen, setImportModalOpen,
    // bulk
    selectedIds, setSelectedIds, bulkDeleting,
    isAllSelected, isSomeSelected, toggleSelectAll, toggleSelect,
    handleBulkDelete,
    // lifecycle
    handleActivate, handleDeactivate, handleTerminate, handleDelete,
    // dup
    duplicates, showDuplicates, setShowDuplicates,
    scanningDuplicates, handleScanDuplicates,
    mergeGroup, setMergeGroup,
    // helpers
    getStatusBadge,
  };
}
